import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ShoppingCart, 
  Plus, 
  Check, 
  Star, 
  Store, 
  Tag, 
  Beef, 
  Loader2,
  Sparkles,
  TrendingDown,
  RefreshCw,
  MapPin,
  Clock,
  Filter,
  List,
  Trash2,
  BookOpen,
  ChefHat,
  ArrowRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const GroceryDeals = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [deals, setDeals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToList, setAddingToList] = useState(null);
  const [addedDeals, setAddedDeals] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [storeFilter, setStoreFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [geoLoading, setGeoLoading] = useState(false);
  
  // Shopping list state
  const [myList, setMyList] = useState([]);
  const [showListDialog, setShowListDialog] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  
  // Matching recipes state
  const [matchingRecipes, setMatchingRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDeals();
      fetchMyList();
    }
  }, [user]);

  const fetchDeals = async () => {
    try {
      const response = await api.get('/deals');
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyList = async () => {
    setLoadingList(true);
    try {
      const response = await api.get('/deals/my-list');
      setMyList(response.data.deal_items || []);
      // Mark added deals
      const addedIds = new Set(response.data.deal_items?.map(item => item.deal_id) || []);
      setAddedDeals(addedIds);
    } catch (error) {
      console.error('Error fetching list:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchMatchingRecipes = async () => {
    if (myList.length === 0) {
      setMatchingRecipes([]);
      return;
    }
    
    setLoadingRecipes(true);
    try {
      // Get deal names for matching
      // Defensive coerce — older grocery list rows can have item.name as an
      // object instead of a string (data migration leftover). Use String()
      // to never crash with "(e||'').toLowerCase is not a function".
      const dealNames = myList.map(item => String(item?.name ?? '').toLowerCase()).filter(Boolean);
      const response = await api.get('/deals/matching-recipes');
      setMatchingRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching matching recipes:', error);
    } finally {
      setLoadingRecipes(false);
    }
  };

  useEffect(() => {
    if (showListDialog && myList.length > 0) {
      fetchMatchingRecipes();
    }
  }, [showListDialog, myList]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/deals/refresh');
      await fetchDeals();
      toast.success(language === 'fr' ? 'Aubaines mises à jour!' : 'Deals updated!');
    } catch (error) {
      console.error('Error refreshing deals:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddToList = async (deal) => {
    setAddingToList(deal.id);
    try {
      await api.post('/deals/add-to-list', {
        deal_id: deal.id,
        deal_name: deal.name,
        store: deal.store,
        store_name: deal.store_name,
        sale_price: deal.sale_price,
        unit: deal.unit,
        quantity: 1
      });
      setAddedDeals(prev => new Set([...prev, deal.id]));
      setMyList(prev => [...prev, {
        deal_id: deal.id,
        name: deal.name,
        store: deal.store,
        store_name: deal.store_name,
        sale_price: deal.sale_price,
        unit: deal.unit
      }]);
      toast.success(
        language === 'fr' 
          ? `${deal.name} ajouté à ta liste!` 
          : `${deal.name} added to your list!`
      );
    } catch (error) {
      console.error('Error adding to list:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setAddingToList(null);
    }
  };

  const handleRemoveFromList = async (dealId) => {
    try {
      await api.delete(`/deals/remove-from-list/${dealId}`);
      setMyList(prev => prev.filter(item => item.deal_id !== dealId));
      setAddedDeals(prev => {
        const newSet = new Set(prev);
        newSet.delete(dealId);
        return newSet;
      });
      toast.success(language === 'fr' ? 'Retiré de la liste' : 'Removed from list');
    } catch (error) {
      console.error('Error removing from list:', error);
    }
  };

  // Geolocation
  const handleGeolocate = async () => {
    if (!navigator.geolocation) {
      toast.error(language === 'fr' ? 'Géolocalisation non supportée' : 'Geolocation not supported');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Reverse geocode to get postal code
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { 'Accept-Language': 'fr' } }
          );
          const geoData = await geoRes.json();
          const postalCode = geoData?.address?.postcode;
          if (postalCode) {
            await api.post('/deals/set-location', { postal_code: postalCode });
            updateUser({ ...user, postal_code: postalCode });
            // Refresh deals with new postal code
            setRefreshing(true);
            await api.post('/deals/refresh');
            await fetchDeals();
            setRefreshing(false);
            toast.success(
              language === 'fr'
                ? `Position: ${postalCode} — Aubaines mises à jour!`
                : `Location: ${postalCode} — Deals updated!`
            );
          } else {
            toast.error(language === 'fr' ? 'Code postal non trouvé' : 'Postal code not found');
          }
        } catch (err) {
          console.error('Geo error:', err);
          toast.error(language === 'fr' ? 'Erreur de géolocalisation' : 'Geolocation error');
        } finally {
          setGeoLoading(false);
        }
      },
      () => {
        setGeoLoading(false);
        toast.error(language === 'fr' ? 'Accès à la position refusé' : 'Location access denied');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  // Filter deals
  const getFilteredDeals = () => {
    if (!deals?.all_deals) return [];
    let filtered = deals.all_deals;
    if (storeFilter !== 'all') {
      filtered = filtered.filter(d => d.store === storeFilter);
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(d => d.category_id === categoryFilter);
    }
    return filtered;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  const DealCard = ({ deal, featured = false }) => (
    <div
      className={featured ? 'col-span-full' : ''}
    >
      <Card className={`bg-card border-border hover:border-primary/50 transition-all ${featured ? 'bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30' : ''}`}>
        <CardContent className={`p-4 ${featured ? 'md:p-6' : ''}`}>
          <div className="flex items-start gap-4">
            {/* Store Logo */}
            <div 
              className={`flex-shrink-0 ${featured ? 'w-16 h-16 text-3xl' : 'w-12 h-12 text-2xl'} rounded-xl flex items-center justify-center`}
              style={{ backgroundColor: `${deal.store_color}20` }}
            >
              {deal.store_logo}
            </div>
            
            {/* Deal Info */}
            <div className="flex-1 min-w-0">
              {featured && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-primary text-primary-foreground font-heading uppercase text-xs">
                    <Star className="w-3 h-3 mr-1" />
                    {language === 'fr' ? 'Deal du Coach' : "Coach's Deal"}
                  </Badge>
                </div>
              )}
              
              <h3 className={`font-heading ${featured ? 'text-lg md:text-xl' : 'text-base'} text-foreground truncate`}>
                {deal.name}
              </h3>
              
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{deal.store_name}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-secondary">
                  {deal.icon} {language === 'fr' ? deal.category_name_fr : deal.category_name_en}
                </span>
              </div>
              
              {/* Price */}
              <div className="flex items-center gap-3 mt-3">
                <span className={`font-heading ${featured ? 'text-2xl md:text-3xl' : 'text-xl'} text-primary`}>
                  {deal.sale_price.toFixed(2)}$
                </span>
                <span className="text-sm text-muted-foreground line-through">
                  {deal.original_price.toFixed(2)}$
                </span>
                <Badge variant="outline" className="border-green-500 text-green-500">
                  <TrendingDown className="w-3 h-3 mr-1" />
                  -{deal.discount_percent}%
                </Badge>
              </div>
              
              {/* Protein info */}
              {deal.protein_per_dollar > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  <Beef className="w-3 h-3 inline mr-1" />
                  {deal.protein_per_100g}g protéines/100g • {deal.protein_per_dollar}g/$
                </p>
              )}
            </div>
            
            {/* Add Button */}
            <div className="flex-shrink-0">
              <Button
                onClick={() => handleAddToList(deal)}
                disabled={addingToList === deal.id || addedDeals.has(deal.id)}
                size={featured ? 'default' : 'sm'}
                className={addedDeals.has(deal.id) 
                  ? 'bg-green-600 hover:bg-green-600' 
                  : 'bg-primary hover:bg-primary/90'
                }
                data-testid={`add-deal-${deal.id}`}
              >
                {addingToList === deal.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : addedDeals.has(deal.id) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="grocery-deals-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-foreground mb-2">
                {language === 'fr' ? 'Chasse aux Aubaines' : 'Deal Hunter'}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {user?.postal_code
                  ? (language === 'fr' ? `Région ${user.postal_code}` : `Area ${user.postal_code}`)
                  : (language === 'fr' ? 'Québec • IGA, Métro, Maxi, Super C, Provigo' : 'Quebec • IGA, Metro, Maxi, Super C, Provigo')}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Geolocation */}
              <Button
                onClick={handleGeolocate}
                disabled={geoLoading}
                variant="outline"
                size="sm"
                className="border-border"
                data-testid="geolocate-btn"
              >
                {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                <span className="hidden sm:inline ml-1.5">{language === 'fr' ? 'Ma position' : 'My location'}</span>
              </Button>
              
              {/* My List Button */}
              <Button
                onClick={() => setShowListDialog(true)}
                variant="outline"
                className="border-primary text-primary relative"
                size="sm"
                data-testid="view-my-list-btn"
              >
                <List className="w-4 h-4 mr-1.5" />
                {language === 'fr' ? 'Liste' : 'List'}
                {myList.length > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-primary text-primary-foreground h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {myList.length}
                  </Badge>
                )}
              </Button>
              
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="border-border"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Protein Target Info */}
          {deals?.protein_target && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/30">
              <p className="text-sm text-foreground">
                <Sparkles className="w-4 h-4 inline mr-2 text-primary" />
                {language === 'fr' 
                  ? `Objectif: ${deals.protein_target.min_g}g - ${deals.protein_target.max_g}g de protéines/jour`
                  : `Target: ${deals.protein_target.min_g}g - ${deals.protein_target.max_g}g protein/day`
                }
              </p>
            </div>
          )}
          
          {/* Flyer Validity + Source */}
          {deals?.flyer_validity && (
            <div className="mt-3 p-3 bg-secondary rounded-lg border border-border flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">
                <Clock className="w-4 h-4 inline mr-2" />
                {language === 'fr' 
                  ? `Circulaire: ${deals.flyer_validity.start} au ${deals.flyer_validity.end}`
                  : `Flyer: ${deals.flyer_validity.start} to ${deals.flyer_validity.end}`
                }
              </p>
              <Badge variant="outline" className="border-green-500 text-green-500">
                {deals.total_deals} {language === 'fr' ? 'aubaines en direct' : 'live deals'}
              </Badge>
            </div>
          )}

          {/* Store Filter Chips */}
          {deals?.deals_by_store && (
            <div className="mt-4 flex gap-2 flex-wrap" data-testid="store-filters">
              <Button
                variant={storeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStoreFilter('all')}
                className="h-8 text-xs"
                data-testid="store-filter-all"
              >
                {language === 'fr' ? 'Toutes les bannières' : 'All stores'}
              </Button>
              {Object.entries(deals.deals_by_store).map(([storeId, storeData]) => (
                <Button
                  key={storeId}
                  variant={storeFilter === storeId ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStoreFilter(storeFilter === storeId ? 'all' : storeId)}
                  className="h-8 text-xs"
                  data-testid={`store-filter-${storeId}`}
                >
                  {storeData.store_logo} {storeData.store_name}
                  <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                    {storeData.deals.length}
                  </Badge>
                </Button>
              ))}
            </div>
          )}

          {/* Category Filter Chips */}
          {deals?.deals_by_category && (
            <div className="mt-2 flex gap-2 flex-wrap" data-testid="category-filters">
              <Button
                variant={categoryFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter('all')}
                className="h-8 text-xs"
                data-testid="category-filter-all"
              >
                {language === 'fr' ? 'Toutes catégories' : 'All categories'}
              </Button>
              {Object.entries(deals.deals_by_category).map(([catId, catData]) => (
                <Button
                  key={catId}
                  variant={categoryFilter === catId ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter(categoryFilter === catId ? 'all' : catId)}
                  className="h-8 text-xs"
                  data-testid={`category-filter-${catId}`}
                >
                  {catData.icon} {language === 'fr' ? catData.category_name_fr : catData.category_name_en}
                  <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                    {catData.deals.length}
                  </Badge>
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Coach's Deal */}
        {deals?.coach_deal && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8"
          >
            <DealCard deal={deals.coach_deal} featured={true} />
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-secondary mb-6 flex-wrap h-auto p-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Filter className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Tous' : 'All'}
            </TabsTrigger>
            <TabsTrigger value="stores" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Store className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Par Magasin' : 'By Store'}
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Tag className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Par Catégorie' : 'By Category'}
            </TabsTrigger>
          </TabsList>

          {/* All Deals */}
          <TabsContent value="all">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredDeals().filter(d => d.id !== deals?.coach_deal?.id).map((deal) => (
                <DealCard key={deal.id} deal={deal} />
              ))}
            </div>
            {getFilteredDeals().length === 0 && (storeFilter !== 'all' || categoryFilter !== 'all') && (
              <div className="text-center py-12">
                <Filter className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Aucune aubaine avec ces filtres' : 'No deals match these filters'}
                </p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => { setStoreFilter('all'); setCategoryFilter('all'); }}>
                  {language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* By Store */}
          <TabsContent value="stores">
            <div className="space-y-8">
              {deals?.deals_by_store && Object.entries(deals.deals_by_store).map(([storeId, storeData]) => (
                <div key={storeId}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{storeData.store_logo}</span>
                    <h2 className="font-heading text-xl uppercase tracking-tight text-foreground">
                      {storeData.store_name}
                    </h2>
                    <Badge variant="outline" className="ml-auto">
                      {storeData.deals.length} {language === 'fr' ? 'aubaines' : 'deals'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {storeData.deals.map((deal) => (
                      <DealCard key={deal.id} deal={deal} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* By Category */}
          <TabsContent value="categories">
            <div className="space-y-8">
              {deals?.deals_by_category && Object.entries(deals.deals_by_category).map(([catId, catData]) => (
                <div key={catId}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{catData.icon}</span>
                    <h2 className="font-heading text-xl uppercase tracking-tight text-foreground">
                      {language === 'fr' ? catData.category_name_fr : catData.category_name_en}
                    </h2>
                    <Badge variant="outline" className="ml-auto">
                      {catData.deals.length} {language === 'fr' ? 'aubaines' : 'deals'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catData.deals.map((deal) => (
                      <DealCard key={deal.id} deal={deal} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Empty State */}
        {(!deals?.all_deals || deals.all_deals.length === 0) && (
          <div className="text-center py-20">
            <ShoppingCart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">
              {language === 'fr' ? 'Aucune aubaine disponible' : 'No deals available'}
            </p>
          </div>
        )}

        {/* Last Updated */}
        {deals?.last_updated && (
          <div className="mt-8 text-center text-xs text-muted-foreground">
            <Clock className="w-3 h-3 inline mr-1" />
            {language === 'fr' ? 'Dernière mise à jour: ' : 'Last updated: '}
            {new Date(deals.last_updated).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
          </div>
        )}

        {/* My List Dialog */}
        <Dialog open={showListDialog} onOpenChange={setShowListDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-primary" />
                {language === 'fr' ? 'Ma Liste d\'Aubaines' : 'My Deals List'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* List Items */}
              {myList.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Ta liste est vide' : 'Your list is empty'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'fr' 
                      ? 'Clique sur + pour ajouter des aubaines'
                      : 'Click + to add deals'
                    }
                  </p>
                </div>
              ) : (
                <>
                  {/* Group by store */}
                  {Object.entries(
                    myList.reduce((acc, item) => {
                      const store = item.store || 'other';
                      if (!acc[store]) acc[store] = { store_name: item.store_name, items: [] };
                      acc[store].items.push(item);
                      return acc;
                    }, {})
                  ).map(([storeId, storeData]) => (
                    <div key={storeId} className="space-y-2">
                      <h3 className="font-heading text-sm uppercase tracking-tight text-primary flex items-center gap-2">
                        <Store className="w-4 h-4" />
                        {storeData.store_name}
                      </h3>
                      {storeData.items.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-foreground font-medium">{item.name}</p>
                            <p className="text-sm text-primary font-bold">
                              {item.sale_price?.toFixed(2)}$ <span className="text-muted-foreground font-normal">{item.unit}</span>
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromList(item.deal_id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Total */}
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {language === 'fr' ? 'Total estimé:' : 'Estimated total:'}
                      </span>
                      <span className="font-heading text-2xl text-primary">
                        {myList.reduce((sum, item) => sum + (item.sale_price || 0), 0).toFixed(2)}$
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Matching Recipes Section */}
              {myList.length > 0 && (
                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
                      <ChefHat className="w-5 h-5 text-primary" />
                      {language === 'fr' ? 'Recettes Suggérées' : 'Suggested Recipes'}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/recipes')}
                      className="border-primary text-primary"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Voir toutes' : 'View all'}
                    </Button>
                  </div>

                  {loadingRecipes ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    </div>
                  ) : matchingRecipes.length > 0 ? (
                    <div className="space-y-3">
                      {matchingRecipes.slice(0, 3).map((recipe, idx) => (
                        <Card 
                          key={idx}
                          className="bg-secondary/50 border-border hover:border-primary/50 cursor-pointer transition-colors"
                          onClick={() => {
                            setShowListDialog(false);
                            navigate('/recipes');
                          }}
                        >
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                              <ChefHat className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-foreground">{recipe.title}</h4>
                              <p className="text-sm text-muted-foreground">
                                {recipe.protein_g}g protéines • {recipe.calories} cal
                              </p>
                              {recipe.matching_ingredients && (
                                <p className="text-xs text-primary mt-1">
                                  {language === 'fr' ? 'Contient:' : 'Contains:'} {recipe.matching_ingredients.join(', ')}
                                </p>
                              )}
                            </div>
                            <ArrowRight className="w-5 h-5 text-muted-foreground" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-secondary/30 rounded-lg">
                      <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' 
                          ? 'Ajoute des recettes à ton livre pour voir les suggestions!'
                          : 'Add recipes to your book to see suggestions!'
                        }
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowListDialog(false);
                          navigate('/recipes');
                        }}
                        className="mt-3"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Ajouter des recettes' : 'Add recipes'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default GroceryDeals;
